import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock, AlertTriangle, CheckCircle2, Flag, ChevronLeft, ChevronRight,
  Download, FileImage, FileText, Trophy, BookOpen, Shield, Target, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import {
  shuffle
} from '@/data/finalAssessmentQuestions';
import { getAssessmentBank } from '@/data/assessments';
import { useCourseLanguage, getLangInfo } from '@/data/languages';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { downloadPNG, downloadPDF, gradeFromPercent } from '@/utils/assessmentReport';
import { logActivity, getLearnerId } from '@/utils/api';

const TOTAL_SECONDS = 60 * 60; // 60 minutes

const buildAssessmentId = (learnerId) => {
  const prefix = (learnerId || 'guest').split('-')[0] || 'guest';
  const t = Date.now().toString(36).toUpperCase();
  return `KFA-${prefix.toUpperCase()}-${t}`;
};

const FinalAssessment = () => {
  const navigate = useNavigate();
  const learnerId = getLearnerId();
  const learnerName = (typeof window !== 'undefined' ? localStorage.getItem('studentName') : '') || 'Learner';

  useEffect(() => {
    if (!learnerId) navigate('/');
  }, [learnerId, navigate]);

  const [phase, setPhase] = useState('intro'); // 'intro' | 'active' | 'result'
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({}); // qid -> optionIndex
  const [marked, setMarked] = useState({}); // qid -> true
  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [result, setResult] = useState(null);
  const [downloading, setDownloading] = useState(null); // 'png' | 'pdf' | null
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [lang] = useCourseLanguage();
  const [activeLang, setActiveLang] = useState(lang);
  const shownLang = phase === 'intro' ? lang : activeLang;
  const { questions: BANK, categories: CATEGORIES } = getAssessmentBank(shownLang);
  const langLabel = getLangInfo(shownLang).label;
  const getCategoryLabel = (id) => CATEGORIES.find(c => c.id === id)?.label || id;
  const timerRef = useRef(null);
  const activityLoggedRef = useRef(false);

  // Freeze body scroll in active phase for better focus
  useEffect(() => {
    if (phase === 'active') {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [phase]);

  const startAssessment = () => {
    // Shuffle all 250 questions + shuffle options per question
    const seed = Date.now();
    setActiveLang(lang);
    const shuffled = shuffle(getAssessmentBank(lang).questions, seed).map((q) => {
      const opts = q.options.map((text, i) => ({ text, originalIdx: i }));
      const shuffledOpts = shuffle(opts);
      return {
        ...q,
        shuffledOptions: shuffledOpts,
        correctShuffledIdx: shuffledOpts.findIndex(o => o.originalIdx === q.correctIndex),
      };
    });
    setQuestions(shuffled);
    setIdx(0);
    setAnswers({});
    setMarked({});
    setSecondsLeft(TOTAL_SECONDS);
    setResult(null);
    setPhase('active');
  };

  const computeResult = useCallback(() => {
    let score = 0;
    const perCat = {};
    CATEGORIES.forEach(c => { perCat[c.id] = { label: c.label, correct: 0, total: 0, attempted: 0 }; });
    questions.forEach((q) => {
      perCat[q.category].total += 1;
      const picked = answers[q.id];
      if (picked !== undefined && picked !== null) perCat[q.category].attempted += 1;
      if (picked === q.correctShuffledIdx) {
        score += 1;
        perCat[q.category].correct += 1;
      }
    });
    const review = questions.map((q, i) => ({
      n: i + 1,
      category: CATEGORIES.find(c => c.id === q.category)?.label || q.category,
      q: q.q,
      options: q.shuffledOptions.map(o => o.text),
      picked: answers[q.id] ?? null,
      correct: q.correctShuffledIdx,
      explanation: q.explanation,
    }));
    const total = questions.length;
    const percent = total ? Math.round((score / total) * 100) : 0;
    const grade = gradeFromPercent(percent);
    const timeTakenSec = TOTAL_SECONDS - secondsLeft;
    const assessmentId = buildAssessmentId(learnerId);
    return {
      score, total, percent, grade,
      categoryBreakdown: CATEGORIES.map(c => perCat[c.id]),
      timeTakenMin: Math.max(1, Math.round(timeTakenSec / 60)),
      timeTakenSec,
      review,
      dateIso: new Date().toISOString(),
      trackLabel: langLabel,
      assessmentId,
    };
  }, [questions, answers, secondsLeft, learnerId, CATEGORIES, langLabel]);

  const submitAssessment = useCallback(async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    const r = computeResult();
    setResult(r);
    setPhase('result');
    setConfirmSubmit(false);
    // Log activity to backend (idempotent per learner+type+reference_id handled server-side)
    if (learnerId && !activityLoggedRef.current) {
      activityLoggedRef.current = true;
      await logActivity('final_assessment_completed', r.assessmentId, r.score, r.total, r.timeTakenSec, activeLang);
    }
  }, [computeResult, learnerId, activeLang]);

  const submitRef = useRef(submitAssessment);
  submitRef.current = submitAssessment;

  // Timer
  useEffect(() => {
    if (phase !== 'active') return;
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          // Auto-submit on timeout
          setTimeout(() => submitRef.current(), 0);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]);

  const fmtTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const selectAnswer = (optIdx) => {
    if (phase !== 'active') return;
    const q = questions[idx];
    setAnswers(prev => ({ ...prev, [q.id]: optIdx }));
  };

  const toggleMark = () => {
    const q = questions[idx];
    setMarked(prev => ({ ...prev, [q.id]: !prev[q.id] }));
  };

  const go = (delta) => {
    setIdx((i) => Math.min(questions.length - 1, Math.max(0, i + delta)));
  };

  const jump = (i) => setIdx(i);

  const renderPaletteGrid = (prefix, onPick) => (
    <div className="grid grid-cols-5 gap-1.5">
      {questions.map((qq, i) => {
        const isAns = answers[qq.id] !== undefined;
        const isMk = !!marked[qq.id];
        let bg = 'bg-[#1A1A20] text-[#71717A] border-white/10';
        if (i === idx) bg = 'bg-[#FFB800] text-black border-[#FFB800]';
        else if (isMk) bg = 'bg-[#EC4899]/80 text-white border-[#EC4899]';
        else if (isAns) bg = 'bg-[#22C55E]/80 text-black border-[#22C55E]';
        return (
          <button
            key={qq.id}
            data-testid={`${prefix}-${i}`}
            onClick={() => { jump(i); onPick?.(); }}
            className={`aspect-square rounded border text-[11px] font-code font-bold hover:scale-105 transition-transform ${bg}`}
          >
            {i + 1}
          </button>
        );
      })}
    </div>
  );

  const answeredCount = Object.keys(answers).length;
  const markedCount = Object.values(marked).filter(Boolean).length;

  const reportData = useMemo(() => {
    if (!result) return null;
    return { learnerName, ...result };
  }, [result, learnerName]);

  const handleDownload = async (kind) => {
    if (!reportData) return;
    setDownloading(kind);
    try {
      if (kind === 'png') await downloadPNG(reportData);
      else if (kind === 'pdf') await downloadPDF(reportData);
    } catch (e) {
      console.error(e);
    } finally {
      setTimeout(() => setDownloading(null), 400);
    }
  };

  // ---------- INTRO ----------
  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col">
        <Header />
        <main data-testid="assessment-intro" className="pt-24 pb-12 max-w-4xl mx-auto px-6 flex-1 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-10"
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FFB800]/10 to-[#EC4899]/10 border border-[#FFB800]/30 rounded-full px-4 py-1.5 mb-5">
              <Trophy className="w-4 h-4 text-[#FFB800]" />
              <span className="text-[#FFB800] text-xs font-code tracking-widest">FINAL · PLACEMENT · READINESS</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight">
              Final <span className="bg-gradient-to-r from-[#FFB800] to-[#EC4899] bg-clip-text text-transparent">Assessment</span>
            </h1>
            <p className="text-[#A1A1AA] font-body max-w-2xl mx-auto">
              {BANK.length} industry-oriented {langLabel} track MCQs · 60 minutes · {CATEGORIES.length} technical domains covering what every top-tier placement expects.
            </p>
            <div className="mt-6 flex flex-col items-center gap-2">
              <span className="text-[#71717A] text-xs font-code tracking-widest">ASSESSMENT TRACK</span>
              <LanguageSwitcher size="lg" testIdPrefix="assessment-lang" />
            </div>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {[
              { icon: Target, label: 'Questions', value: '250', color: '#FFB800' },
              { icon: Clock, label: 'Duration', value: '60 min', color: '#007AFF' },
              { icon: Shield, label: 'Categories', value: '11', color: '#EC4899' },
            ].map((s) => (
              <div key={s.label} className="bg-[#0F0F12] border border-white/10 rounded-xl p-5 flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                  style={{ background: s.color + '20', border: `1px solid ${s.color}40` }}
                >
                  <s.icon className="w-6 h-6" style={{ color: s.color }} />
                </div>
                <div>
                  <div className="text-[#71717A] text-xs font-code">{s.label.toUpperCase()}</div>
                  <div className="text-white text-2xl font-heading font-bold">{s.value}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-[#0F0F12] border border-white/10 rounded-2xl p-6 mb-8">
            <h2 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#FFB800]" /> Rules & Format
            </h2>
            <ul className="space-y-2 text-[#A1A1AA] font-body text-sm">
              <li className="flex gap-3"><span className="text-[#FFB800] font-code">•</span> Each MCQ has 4 options with exactly one correct answer. No negative marking.</li>
              <li className="flex gap-3"><span className="text-[#FFB800] font-code">•</span> You can freely navigate between questions and mark any for review.</li>
              <li className="flex gap-3"><span className="text-[#FFB800] font-code">•</span> Questions are shuffled every attempt — no two sessions are identical.</li>
              <li className="flex gap-3"><span className="text-[#FFB800] font-code">•</span> Timer auto-submits at 0:00. You can submit early anytime.</li>
              <li className="flex gap-3"><span className="text-[#FFB800] font-code">•</span> You'll receive a downloadable scorecard (PNG + PDF) with full category breakdown.</li>
            </ul>
          </div>

          <div className="bg-[#0F0F12] border border-white/10 rounded-2xl p-6 mb-8">
            <h2 className="font-heading text-xl font-semibold text-white mb-4">Topic Coverage</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {CATEGORIES.map((c) => (
                <div key={c.id} className="bg-[#1A1A20] rounded-lg px-3 py-2 flex items-center justify-between">
                  <span className="text-white text-sm font-body">{c.label}</span>
                  <Badge className="bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/20 border font-code text-xs">
                    {c.count}
                  </Badge>
                </div>
              ))}
            </div>
          </div>

          <Button
            data-testid="start-assessment-button"
            onClick={startAssessment}
            className="w-full bg-gradient-to-r from-[#FFB800] to-[#FF8800] hover:from-[#FFC820] hover:to-[#FF9820] text-black font-heading font-bold py-7 text-lg shadow-[0_0_60px_rgba(255,184,0,0.3)]"
          >
            Start Assessment Now
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  // ---------- ACTIVE ----------
  if (phase === 'active') {
    const q = questions[idx];
    if (!q) return null;
    const selected = answers[q.id];
    const isMarked = !!marked[q.id];
    const lowTime = secondsLeft < 300; // last 5 min
    const criticalTime = secondsLeft < 60;

    return (
      <div
        data-testid="assessment-active"
        className="fixed inset-0 bg-[#050505] flex flex-col overflow-hidden"
      >
        {/* Top bar: timer + progress */}
        <div className="h-16 border-b border-white/10 bg-[#050505]/90 backdrop-blur-xl flex items-center justify-between gap-2 px-3 sm:px-6 flex-shrink-0 z-30">
          <div className="flex items-center gap-3 min-w-0">
            <div className="hidden sm:flex w-8 h-8 rounded-lg bg-gradient-to-br from-[#FFB800] to-[#FF8800] flex items-center justify-center">
              <Trophy className="w-4 h-4 text-black" />
            </div>
            <div className="min-w-0">
              <div className="text-white font-heading font-semibold text-sm truncate">Final Assessment</div>
              <div className="text-[#71717A] text-xs font-code truncate" data-testid="assessment-answered-count">
                <span className="hidden sm:inline">{learnerName} · </span>{answeredCount}/{questions.length} answered
              </div>
            </div>
          </div>

          <div className={`flex items-center gap-2 sm:gap-3 px-2.5 sm:px-4 py-2 rounded-lg border font-code font-bold text-base sm:text-lg flex-shrink-0 ${
            criticalTime
              ? 'border-red-500 bg-red-500/10 text-red-400 animate-pulse'
              : lowTime
              ? 'border-[#FFB800] bg-[#FFB800]/10 text-[#FFB800]'
              : 'border-white/10 bg-white/5 text-white'
          }`} data-testid="assessment-timer">
            <Clock className="w-5 h-5" />
            {fmtTime(secondsLeft)}
          </div>

          <Button
            data-testid="open-palette-button"
            onClick={() => setPaletteOpen(true)}
            variant="outline"
            size="sm"
            className="lg:hidden border-white/10 text-white hover:bg-white/5 font-code flex-shrink-0"
          >
            {idx + 1}/{questions.length}
          </Button>
          <Button
            data-testid="submit-assessment-button"
            onClick={() => setConfirmSubmit(true)}
            className="bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 font-heading"
            variant="outline"
          >
            Submit
          </Button>
        </div>

        {/* Body: question + palette */}
        <div className="flex-1 grid lg:grid-cols-[1fr_300px] gap-0 overflow-hidden">
          {/* Question panel */}
          <div className="overflow-y-auto p-4 sm:p-8">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-[#71717A] font-code text-xs tracking-widest mb-1">
                    QUESTION {idx + 1} OF {questions.length}
                  </div>
                  <Badge className="bg-[#007AFF]/10 text-[#007AFF] border-[#007AFF]/20 border font-code">
                    {getCategoryLabel(q.category)}
                  </Badge>
                </div>
                <Button
                  onClick={toggleMark}
                  data-testid="mark-review-button"
                  variant="outline"
                  size="sm"
                  className={`border font-code ${
                    isMarked
                      ? 'bg-[#EC4899]/10 border-[#EC4899]/40 text-[#EC4899]'
                      : 'border-white/10 text-white hover:bg-white/5'
                  }`}
                >
                  <Flag className="w-4 h-4 mr-2" />
                  {isMarked ? 'Marked for Review' : 'Mark for Review'}
                </Button>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={q.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <h2
                    data-testid="assessment-question-text"
                    className="font-heading text-2xl font-semibold text-white mb-6 leading-snug"
                  >
                    {q.q}
                  </h2>

                  <div className="space-y-3 mb-8">
                    {q.shuffledOptions.map((opt, i) => (
                      <button
                        key={i}
                        data-testid={`assessment-option-${i}`}
                        onClick={() => selectAnswer(i)}
                        className={`w-full text-left px-4 py-4 rounded-lg border font-body transition-all duration-150 ${
                          selected === i
                            ? 'border-[#FFB800] bg-[#FFB800]/10 text-white'
                            : 'border-white/10 bg-[#0F0F12] text-[#A1A1AA] hover:border-white/30 hover:text-white'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`w-8 h-8 rounded-full border flex items-center justify-center text-sm font-code font-bold flex-shrink-0 ${
                            selected === i ? 'bg-[#FFB800] text-black border-[#FFB800]' : 'border-current'
                          }`}>
                            {String.fromCharCode(65 + i)}
                          </span>
                          <span className="flex-1">{opt.text}</span>
                          {selected === i && <CheckCircle2 className="w-5 h-5 text-[#FFB800]" />}
                        </div>
                      </button>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="flex items-center justify-between gap-4">
                <Button
                  onClick={() => go(-1)}
                  disabled={idx === 0}
                  data-testid="prev-question-button"
                  variant="outline"
                  className="border-white/10 text-white hover:bg-white/5 disabled:opacity-30"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" /> Previous
                </Button>
                <Button
                  onClick={() => go(1)}
                  disabled={idx === questions.length - 1}
                  data-testid="next-question-button"
                  className="bg-[#FFB800] text-black hover:bg-[#FFC820] font-heading font-bold disabled:opacity-30"
                >
                  Next <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>

          {/* Palette */}
          <div className="hidden lg:flex flex-col border-l border-white/10 bg-[#0A0A0D] overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <div className="text-white font-heading font-semibold text-sm mb-3">Question Palette</div>
              <div className="grid grid-cols-2 gap-2 text-xs font-code">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-sm bg-[#22C55E]"></span>
                  <span className="text-[#71717A]">Answered · {answeredCount}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-sm bg-[#EC4899]"></span>
                  <span className="text-[#71717A]">Marked · {markedCount}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-sm bg-[#FFB800]"></span>
                  <span className="text-[#71717A]">Current</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-sm bg-[#1A1A20] border border-white/10"></span>
                  <span className="text-[#71717A]">Unseen</span>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {renderPaletteGrid('palette-q')}
            </div>
          </div>
        </div>

        <AnimatePresence>
          {paletteOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/80 backdrop-blur-sm z-40 flex justify-end"
              onClick={() => setPaletteOpen(false)}
              data-testid="mobile-palette-overlay"
            >
              <motion.div
                initial={{ x: 320 }}
                animate={{ x: 0 }}
                exit={{ x: 320 }}
                transition={{ type: 'tween', duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
                className="w-72 max-w-[85vw] h-full bg-[#0A0A0D] border-l border-white/10 flex flex-col"
              >
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                  <div>
                    <div className="text-white font-heading font-semibold text-sm">Question Palette</div>
                    <div className="text-[#71717A] text-xs font-code">Answered {answeredCount} · Marked {markedCount}</div>
                  </div>
                  <Button size="sm" variant="ghost" className="text-white" data-testid="close-palette-button" onClick={() => setPaletteOpen(false)}>
                    Close
                  </Button>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  {renderPaletteGrid('mobile-palette-q', () => setPaletteOpen(false))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Submit confirmation modal */}
        <AnimatePresence>
          {confirmSubmit && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setConfirmSubmit(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-[#0F0F12] border border-white/10 rounded-2xl p-6 max-w-md w-full"
              >
                <div className="flex items-start gap-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-[#FFB800] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-white font-heading text-xl font-bold mb-1">Submit assessment?</h3>
                    <p className="text-[#A1A1AA] text-sm font-body">
                      You've answered <span className="text-white font-bold">{answeredCount}</span> of {questions.length} questions.
                      {questions.length - answeredCount > 0 && (
                        <> {questions.length - answeredCount} will be scored as unanswered.</>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setConfirmSubmit(false)}
                    variant="outline"
                    className="flex-1 border-white/10 text-white hover:bg-white/5"
                    data-testid="cancel-submit-button"
                  >
                    Keep answering
                  </Button>
                  <Button
                    onClick={submitAssessment}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white font-heading font-bold"
                    data-testid="confirm-submit-button"
                  >
                    Submit now
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // ---------- RESULT ----------
  if (phase === 'result' && result) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col">
        <Header />
        <main
          data-testid="assessment-result"
          className="pt-24 pb-12 max-w-5xl mx-auto px-6 flex-1 w-full"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-10"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', damping: 12, delay: 0.2 }}
              className="w-28 h-28 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{
                background: result.percent >= 70
                  ? 'linear-gradient(135deg, #FFB800, #FF8800)'
                  : result.percent >= 50
                  ? 'linear-gradient(135deg, #007AFF, #0055CC)'
                  : 'linear-gradient(135deg, #71717A, #52525B)',
                boxShadow: '0 0 60px rgba(255,184,0,0.4)',
              }}
            >
              <Trophy className="w-14 h-14 text-white" />
            </motion.div>
            <div className="text-[#71717A] font-code text-xs tracking-widest mb-2">ASSESSMENT COMPLETE</div>
            <h1 className="font-heading text-5xl font-bold text-white mb-2">
              {result.score}<span className="text-[#71717A]">/{result.total}</span>
            </h1>
            <div className="flex items-center justify-center gap-4 mt-4">
              <Badge className="bg-[#007AFF]/10 text-[#007AFF] border-[#007AFF]/30 border font-code text-base px-4 py-1">
                {result.percent}%
              </Badge>
              <Badge className="bg-[#EC4899]/10 text-[#EC4899] border-[#EC4899]/30 border font-code text-base px-4 py-1">
                Grade {result.grade}
              </Badge>
              <Badge data-testid="result-track-label" className="bg-white/5 text-white border-white/10 border font-code text-base px-4 py-1">
                {result.trackLabel} Track
              </Badge>
              <Badge className="bg-white/5 text-white border-white/10 border font-code text-base px-4 py-1">
                {Math.floor(result.timeTakenSec / 60)}m {result.timeTakenSec % 60}s
              </Badge>
            </div>
          </motion.div>

          {/* Category Breakdown */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-[#0F0F12] border border-white/10 rounded-2xl p-6 mb-8"
          >
            <h2 className="font-heading text-xl font-semibold text-white mb-5">Category Breakdown</h2>
            <div className="space-y-3">
              {result.categoryBreakdown.map((cat, i) => {
                const pct = cat.total ? cat.correct / cat.total : 0;
                let barColor = '#EF4444';
                if (pct >= 0.75) barColor = '#22C55E';
                else if (pct >= 0.5) barColor = '#FFB800';
                else if (pct >= 0.25) barColor = '#F97316';
                return (
                  <div key={i} className="flex items-center gap-4" data-testid={`result-category-${i}`}>
                    <div className="w-48 text-white text-sm font-body truncate flex-shrink-0">{cat.label}</div>
                    <div className="flex-1 h-3 bg-[#1A1A20] rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct * 100}%` }}
                        transition={{ duration: 0.8, delay: 0.4 + i * 0.05 }}
                        className="h-full rounded-full"
                        style={{ background: barColor }}
                      />
                    </div>
                    <div className="w-24 text-right text-white font-code font-bold text-sm">
                      {cat.correct}/{cat.total}
                    </div>
                    <div className="w-14 text-right text-[#71717A] font-code text-xs">
                      {Math.round(pct * 100)}%
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Downloads */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-gradient-to-br from-[#FFB800]/10 via-[#EC4899]/5 to-[#050505] border border-[#FFB800]/30 rounded-2xl p-6 mb-6"
          >
            <div className="flex items-start gap-3 mb-5">
              <Download className="w-6 h-6 text-[#FFB800] flex-shrink-0 mt-1" />
              <div>
                <h2 className="font-heading text-xl font-semibold text-white">Download Your Scorecard</h2>
                <p className="text-[#A1A1AA] text-sm font-body">
                  Share it on LinkedIn, add it to your placement portfolio, or save for your records.
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Button
                onClick={() => handleDownload('png')}
                disabled={downloading !== null}
                data-testid="download-png-button"
                className="bg-[#FFB800] text-black hover:bg-[#FFC820] font-heading font-bold py-6"
              >
                {downloading === 'png' ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Preparing...</>
                ) : (
                  <><FileImage className="w-5 h-5 mr-2" /> Download PNG</>
                )}
              </Button>
              <Button
                onClick={() => handleDownload('pdf')}
                disabled={downloading !== null}
                data-testid="download-pdf-button"
                className="bg-white text-black hover:bg-white/90 font-heading font-bold py-6"
              >
                {downloading === 'pdf' ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Preparing...</>
                ) : (
                  <><FileText className="w-5 h-5 mr-2" /> Download PDF</>
                )}
              </Button>
            </div>
            <div className="mt-4 text-[#71717A] font-code text-xs text-center">
              Assessment ID: {result.assessmentId}
            </div>
          </motion.div>

          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5 font-heading"
            >
              Back to Dashboard
            </Button>
            <Button
              onClick={() => {
                activityLoggedRef.current = false;
                setPhase('intro');
              }}
              variant="outline"
              className="border-[#FFB800]/40 text-[#FFB800] hover:bg-[#FFB800]/10 font-heading"
              data-testid="retake-assessment-button"
            >
              Retake Assessment
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return null;
};

export default FinalAssessment;
